/* eslint-disable no-console -- this module's entire purpose is console output */
/**
 * Hatch Console Diagnostics
 *
 * Prints plain-language explanations of Hatch overlay decisions to the browser
 * console on every booking search. Aimed at non-technical facility owners
 * troubleshooting rule settings.
 *
 * INTEGRATION: the service files wrap their return with `withHatchDiagnostics()`.
 *   return withHatchDiagnostics(request, { location, serviceTypeName, ... })
 *
 * TO DISABLE: replace `withHatchDiagnostics(request, ctx)` with `request`,
 *   or set `ENABLED = false` below for a kill switch.
 *
 * Design constraints:
 *   - Console only. No UI, no window API, no query flags.
 *   - One collapsed group per search. Problems print outside the group.
 *   - Never print a bare reason code — always plain language.
 *   - Always name the decider: HATCH RULES (Hatch) vs GOOSE SETTINGS (Goose).
 *   - Never throws.
 */

/* ═══════════════════════════════════════════════════════════════════════════════
   KILL SWITCH
   ═══════════════════════════════════════════════════════════════════════════════ */

const ENABLED = true

/* ═══════════════════════════════════════════════════════════════════════════════
   TYPES (self-contained)
   ═══════════════════════════════════════════════════════════════════════════════ */

interface HatchSummary {
  rulesMatched: number
  rulesSkipped: number
  offersBlocked: number
  offersRepriced: number
}

interface HatchPricingStep {
  kind: 'surcharge' | 'discount'
  ruleId: string
  label?: string
  impact: number
}

interface PricingCandidate {
  ruleId: string
  kind: 'surcharge' | 'discount'
  impact: number
}

interface HatchOfferPricing {
  changed: boolean
  baseRate: number
  baseTotal: number
  finalRate: number
  finalTotal: number
  nights: number
  petCount: number
  delta: number
  days: unknown[]
  steps: HatchPricingStep[]
  losingCandidates?: PricingCandidate[]
}

interface HatchOfferAvailability {
  baselineAvailable: boolean
  finalAvailable: boolean
  message?: string
  reasonType?: string
  winningRuleId?: string
  affectedPets?: Array<{ locationPetProfileId?: string; id?: string; displayName?: string }>
  days: Array<{ date: string; available: boolean; winningRule?: string; reason?: string }>
  addOns?: Array<{ id?: string; name?: string; available?: boolean; blockingRule?: string }>
}

interface HatchOffer {
  offerId: string
  offerName: string
  changed: boolean
  availability: HatchOfferAvailability
  pricing: HatchOfferPricing
}

interface HatchRule {
  ruleId: string
  status: 'matched' | 'skipped'
  area?: string
  effect?: string
  affectedOffers?: Array<{ name: string; id: string }>
  matchedPets?: Array<{ id: string; displayName?: string }>
  skipReason?: string
  skipDetail?: string
}

interface HatchRuleDef {
  text: string
  area: string
}

interface HatchBlock {
  evaluated: boolean
  summary: HatchSummary
  offers: HatchOffer[]
  rules: HatchRule[]
  ruleDefs: Record<string, HatchRuleDef>
}

/* ═══════════════════════════════════════════════════════════════════════════════
   CONSOLE STYLING
   ═══════════════════════════════════════════════════════════════════════════════ */

const STYLE_HEADER = 'color:#8b5cf6;font-weight:bold'
const STYLE_PROBLEM = 'color:#dc2626;font-weight:bold'
const STYLE_WARN = 'color:#d97706;font-weight:bold'
const STYLE_INFO = 'color:#0891b2'
const STYLE_DIM = 'color:#6b7280'
const STYLE_HATCH = 'color:#7c3aed;font-weight:bold'
const STYLE_GOOSE = 'color:#0d9488;font-weight:bold'

/* ═══════════════════════════════════════════════════════════════════════════════
   PLAIN-LANGUAGE MAPS
   ═══════════════════════════════════════════════════════════════════════════════ */

const REASON_TEXT: Record<string, string> = {
  RULE: 'a Hatch rule',
  WEIGHT: 'pet weight limit',
  TOTAL_WEIGHT: 'combined weight limit for the group',
  PET_COUNT: 'number of pets in the booking',
  TEMPER_TEST: 'temperament evaluation requirement',
  AGE: 'pet age limit',
  BREED: 'breed restriction',
  ALTERED: 'spay/neuter requirement',
  LEAD_TIME: 'how far ahead the booking is',
  PERIOD: 'the dates requested',
  PRICE: 'Goose price cap',
  OCCUPANCY: 'how full the location is',
  SALES_CHANNEL: 'where the booking came from',
  OPERATION_HOUR: 'opening hours',
  CHECK_IN_HOUR: 'check-in time',
  CHECK_OUT_HOUR: 'check-out time',
  MAX_BOOKING_WINDOW: 'how far ahead bookings are allowed',
  SPECIES: 'the species of pet',
  ALLOW: 'an allow rule unlocked this',
  CYCLE_COUNT: 'length of stay',
  QTY: 'quantity available',
  SLOT: 'appointment slot availability',
}

function reasonText(type?: string): string {
  if (!type) return 'no reason given'
  return REASON_TEXT[type] ?? type.toLowerCase().replace(/_/g, ' ')
}

const UNAVAILABLE_TYPE_TEXT: Record<string, string> = {
  INVENTORY: 'fully booked',
  RESTRICTION: 'a booking rule blocked it',
  PRICE: 'price cap',
  REQUIRED_ADDON: 'a required add-on is unavailable',
}

const SKIP_TEXT: Record<string, { what: string; fix: string }> = {
  unsupported_condition: {
    what: 'uses a condition the engine cannot evaluate yet',
    fix: 'this rule has no effect until the engine supports it',
  },
  unsupported_scope: {
    what: 'targets something the engine cannot resolve yet',
    fix: 'this rule has no effect until the engine supports it',
  },
  unsupported_data_source: {
    what: 'needs information the system does not collect yet',
    fix: 'this rule has no effect until that data is wired up',
  },
  incomplete_data: {
    what: 'could not be checked because pet or customer data is missing',
    fix: 'the booking was BLOCKED to be safe — fill in the missing data to unblock it',
  },
  temper_test_self_gate: {
    what: 'was skipped on purpose — it would have blocked the evaluation booking itself',
    fix: 'no action needed, this is intended behaviour',
  },
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SESSION STATE
   ═══════════════════════════════════════════════════════════════════════════════ */

const ruleRegistry = new Map<string, Map<string, HatchRuleDef>>()
const addonRuleIds = new Map<string, Set<string>>()
const printedHashes = new Map<string, string>()
let currentLocation: string | null = null

function resetIfLocationChanged(location: string): void {
  if (currentLocation === location) return
  currentLocation = location
  ruleRegistry.clear()
  addonRuleIds.clear()
  printedHashes.clear()
}

/* ═══════════════════════════════════════════════════════════════════════════════
   INTERNAL TYPES
   ═══════════════════════════════════════════════════════════════════════════════ */

interface AvailabilityEntry {
  available?: boolean
  unavailableReason?: {
    type?: string
    source?: string
    min?: number | null
    max?: number | null
  } | null
  unavailableType?: string | null
  unavailableMsg?: string | null
  price?: { rate?: number | null; value?: number | null } | null
  affectedByPets?: Array<{ displayName?: string; weight?: number | null }> | null
}

interface SearchResultLike {
  offer?: { id?: string; displayName?: string; name?: string; type?: string }
  hasRequiredAddons?: boolean
  availabilityGroups?: Array<{ availabilities?: AvailabilityEntry[] }>
  availabilityGroupsByDays?: Array<Array<{ availabilities?: AvailabilityEntry[] }>>
}

interface ResponseLike {
  results?: SearchResultLike[]
  hatch?: HatchBlock
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PUBLIC API — the only thing services import
   ═══════════════════════════════════════════════════════════════════════════════ */

export interface HatchDiagnosticsContext {
  location: string
  serviceTypeName: string
  apiType: 'client' | 'admin'
  endpoint: 'search' | 'searchByDays'
  params?: {
    type?: string
    required?: boolean | string
    startDate?: string
    endDate?: string
  }
  parentOffer?: string
  pets?: Array<{ displayName?: string; weight?: number }>
}

/**
 * Wrap a search promise with diagnostics reporting.
 *
 * Usage in a service file:
 *   return withHatchDiagnostics(requester(...), { location, serviceTypeName, ... })
 *
 * The response passes through untouched. Rejections are left alone.
 * Safe to call unconditionally — does nothing if ENABLED is false or console
 * is unavailable.
 */
export function withHatchDiagnostics<T>(promise: Promise<T>, ctx: HatchDiagnosticsContext): Promise<T> {
  if (!ENABLED) return promise
  return promise.then((response) => {
    report(ctx, response)
    return response
  })
}

/**
 * Flatten request pet groups into the shape the header renderer wants.
 */
export function petsFromGroups(
  petGroups: Array<{ petCount?: number; petWeights?: number[] }> | undefined
): Array<{ displayName?: string; weight?: number }> {
  const out: Array<{ displayName?: string; weight?: number }> = []
  for (const group of petGroups ?? []) {
    const count = group.petCount ?? group.petWeights?.length ?? 0
    for (let i = 0; i < count; i++) {
      out.push({ weight: group.petWeights?.[i] })
    }
  }
  return out
}

/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════════ */

function firstAvailability(result: SearchResultLike): AvailabilityEntry | undefined {
  const group = result.availabilityGroups?.[0] ?? result.availabilityGroupsByDays?.[0]?.[0]
  return group?.availabilities?.[0]
}

/** True when the unavailable reason was set by the Hatch overlay, not Goose settings. */
function isHatchSourced(reason: AvailabilityEntry['unavailableReason']): boolean {
  return reason?.source === 'hatch'
}

function offerLabel(result: SearchResultLike): string {
  return result.offer?.displayName ?? result.offer?.name ?? result.offer?.id ?? '(unnamed offer)'
}

/* ── Per-day model ────────────────────────────────────────────────────────────
   A /days response carries one entry per calendar day in `availabilityGroupsByDays`,
   and the sidecar mirrors it in `availability.days[]` with the date and winning rule.
   Reading only day 0 (as a single-availability response would) hides every partial
   block — the most common daycare case. These helpers keep the day axis intact.
   ──────────────────────────────────────────────────────────────────────────── */

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Render `2026-08-20` as `Thu Aug 20`.
 *
 * Parsed as UTC on purpose: these are plain calendar dates, and local-time
 * parsing would shift them a day for anyone west of UTC — which would rename
 * the weekday and make a "blocked on Thursdays" rule look wrong.
 */
function formatDay(date: string | undefined): string {
  if (!date) return '(unknown date)'
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  return `${WEEKDAY_NAMES[parsed.getUTCDay()]} ${MONTH_NAMES[parsed.getUTCMonth()]} ${parsed.getUTCDate()}`
}

/** Add `offset` days to a YYYY-MM-DD string. Used when the sidecar omits dates. */
function addDays(start: string | undefined, offset: number): string | undefined {
  if (!start) return undefined
  const parsed = new Date(`${start}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return undefined
  parsed.setUTCDate(parsed.getUTCDate() + offset)
  return parsed.toISOString().slice(0, 10)
}

interface DayVerdict {
  index: number
  date?: string
  available: boolean
  reasonType?: string
  /** True when the Hatch overlay decided this day, rather than the facility. */
  isHatch: boolean
  ruleId?: string
  message?: string
}

/**
 * Build the per-day verdict list, or null when this is not a /days response.
 *
 * Availability comes from the response (what the customer actually gets); the
 * date and winning rule come from the sidecar (what the engine decided). They
 * are zipped by index, and only when the lengths agree — a mismatch means we
 * cannot trust the pairing, so dates degrade to being derived from startDate.
 */
function collectDayVerdicts(
  result: SearchResultLike,
  hatchOffer: HatchOffer | undefined,
  startDate: string | undefined
): DayVerdict[] | null {
  const byDays = result.availabilityGroupsByDays
  if (!byDays || byDays.length === 0) return null

  const sidecarDays = hatchOffer?.availability.days ?? []
  const aligned = sidecarDays.length === byDays.length

  return byDays.map((group, index) => {
    const entry = group?.[0]?.availabilities?.[0]
    const sidecarDay = aligned ? sidecarDays[index] : undefined
    const reason = entry?.unavailableReason
    return {
      index,
      date: sidecarDay?.date ?? addDays(startDate, index),
      available: Boolean(entry?.available),
      reasonType: reason?.type ?? sidecarDay?.reason,
      // The response's `source` is the primary signal; a winning rule on the
      // sidecar day is a fallback for when the two arrays could not be zipped.
      isHatch: isHatchSourced(reason) || Boolean(sidecarDay?.winningRule),
      ruleId: sidecarDay?.winningRule,
      message: entry?.unavailableMsg ?? undefined,
    }
  })
}

type OfferState = 'available' | 'partial' | 'blocked'

/**
 * Classify an offer across the whole requested range.
 *
 * For /days responses this is a day-count decision, not a day-0 lookup: an offer
 * with some open days is bookable ('partial'), and only an offer with no open day
 * at all is 'blocked'.
 */
function offerState(result: SearchResultLike, days: DayVerdict[] | null): OfferState {
  if (days && days.length > 0) {
    const open = days.filter((d) => d.available).length
    if (open === 0) return 'blocked'
    return open < days.length ? 'partial' : 'available'
  }
  return firstAvailability(result)?.available ? 'available' : 'blocked'
}

function hashReport(hatch: HatchBlock | undefined, results: SearchResultLike[]): string {
  const summary = hatch
    ? `${hatch.evaluated}:${hatch.summary.rulesMatched}:${hatch.summary.rulesSkipped}:${hatch.summary.offersBlocked}:${hatch.summary.offersRepriced}`
    : 'none'
  // Day-aware: a change in which days are blocked must defeat the dedupe, or a
  // partial block that shifts dates would print once and never update.
  const verdicts = results
    .map((r) => {
      const byDays = r.availabilityGroupsByDays
      if (byDays && byDays.length > 0) {
        const pattern = byDays.map((g) => (g?.[0]?.availabilities?.[0]?.available ? 1 : 0)).join('')
        return `${r.offer?.id ?? '?'}:${pattern}`
      }
      return `${r.offer?.id ?? '?'}:${firstAvailability(r)?.available ? 1 : 0}`
    })
    .join('|')
  return `${summary}#${verdicts}`
}

function buildHeader(
  input: HatchDiagnosticsContext,
  hatch: HatchBlock | undefined,
  blockedCount: number,
  partialCount: number,
  ruleBlockedDays: number
): string {
  const bits: string[] = [input.serviceTypeName]

  if (input.params?.type) bits.push(input.params.type)
  if (input.params?.required === true || input.params?.required === 'true') bits.push('required')
  if (input.parentOffer) bits.push(`parent=${input.parentOffer}`)
  if (input.endpoint === 'searchByDays') bits.push('/days')

  const dates =
    input.params?.startDate && input.params?.endDate
      ? `${input.params.startDate} \u2192 ${input.params.endDate}`
      : 'no dates'
  bits.push(dates)

  const pets = input.pets ?? []
  if (pets.length > 0) {
    const weights = pets.map((p) => p.weight).filter((w): w is number => w != null)
    bits.push(
      weights.length > 0
        ? `${pets.length} pet${pets.length === 1 ? '' : 's'} (${weights.join(', ')} lbs)`
        : `${pets.length} pet${pets.length === 1 ? '' : 's'}`
    )
  }

  if (!hatch) {
    bits.push('no hatch sidecar')
  } else if (!hatch.evaluated) {
    bits.push('no rules for this location')
  } else {
    bits.push(`${hatch.summary.rulesMatched} rule${hatch.summary.rulesMatched === 1 ? '' : 's'} fired`)
    if (hatch.summary.rulesSkipped > 0) bits.push(`${hatch.summary.rulesSkipped} skipped`)
    if (blockedCount > 0) bits.push(`${blockedCount} offer${blockedCount === 1 ? '' : 's'} blocked`)
    // Partial blocking is the normal daycare outcome and must be visible in the
    // collapsed header, otherwise the only clue is a greyed-out calendar cell.
    if (partialCount > 0) bits.push(`${partialCount} partly blocked`)
    if (ruleBlockedDays > 0) {
      bits.push(`${ruleBlockedDays} day${ruleBlockedDays === 1 ? '' : 's'} blocked by Hatch rules`)
    }
    if (hatch.summary.offersRepriced > 0) bits.push(`${hatch.summary.offersRepriced} repriced`)
  }

  return bits.join(' \u00B7 ')
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SECTION RENDERERS
   ═══════════════════════════════════════════════════════════════════════════════ */

function printCustomerView(
  results: SearchResultLike[],
  hatch: HatchBlock | undefined,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>
): void {
  const rows = results.map((result) => {
    const availability = firstAvailability(result)
    const days = dayMap.get(result) ?? null
    const state = offerState(result, days)
    const reason = availability?.unavailableReason
    const hatchOffer = hatch?.offers.find((o) => o.offerId === result.offer?.id)

    // Who decided. For a partial block the deciders can differ per day, so the
    // column reports whichever layers contributed rather than a single verdict.
    let decidedBy = '\u2014'
    if (state === 'partial' && days) {
      const blockedDays = days.filter((d) => !d.available)
      const byRules = blockedDays.filter((d) => d.isHatch).length
      const byFacility = blockedDays.length - byRules
      const parts: string[] = []
      if (byRules > 0) parts.push(`Hatch rules (${byRules}d)`)
      if (byFacility > 0) parts.push(`Goose (${byFacility}d)`)
      decidedBy = parts.join(' + ')
    } else if (state === 'blocked') {
      const blockedByHatch = days ? days.some((d) => d.isHatch) : isHatchSourced(reason)
      if (blockedByHatch) {
        const ruleId = hatchOffer?.availability.winningRuleId ?? days?.find((d) => d.isHatch)?.ruleId
        decidedBy = ruleId ? `Hatch rule (${ruleId})` : 'Hatch rules'
      } else {
        const reasonType = reason?.type ?? days?.find((d) => !d.available)?.reasonType
        decidedBy = `Goose setting (${reasonText(reasonType)})`
      }
    }

    const rate = availability?.price?.rate
    const priceShown =
      state === 'blocked' || reason?.type === 'PRICE' ? '\u2014' : rate != null ? `$${rate}` : 'no price'

    const customerSees =
      state === 'available' ? 'Select' : state === 'partial' ? 'Select (some days unavailable)' : 'Call to Book'

    const row: Record<string, string> = {
      Offer: offerLabel(result),
      Customer_sees: customerSees,
      Price: priceShown,
      Decided_by: decidedBy,
    }

    if (days) {
      const open = days.filter((d) => d.available).length
      row.Days_open = `${open} of ${days.length}`
    }

    return row
  })

  if (rows.length === 0) {
    console.log('%c(no offers returned)', STYLE_DIM)
    return
  }
  console.log('%c\u{1F4CB} WHAT THE CUSTOMER SEES', STYLE_HEADER)
  console.table(rows)
}

/**
 * Day-by-day breakdown for /days searches, grouped by what caused each block.
 *
 * Grouping by cause rather than listing dates chronologically is deliberate: it
 * makes a recurring pattern ("all four Thursdays") legible at a glance, which is
 * exactly the check an owner needs when verifying a weekday rule.
 */
function printDayBreakdown(
  results: SearchResultLike[],
  hatch: HatchBlock,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>
): void {
  const withDays = results.filter((r) => {
    const days = dayMap.get(r)
    return days && days.some((d) => !d.available)
  })
  if (withDays.length === 0) return

  console.groupCollapsed('%c\u{1F4C5} DAY-BY-DAY AVAILABILITY', STYLE_HEADER)

  for (const result of withDays) {
    const days = dayMap.get(result)!
    const blocked = days.filter((d) => !d.available)
    const open = days.filter((d) => d.available)

    console.group(
      `%c${offerLabel(result)}%c \u2014 ${open.length} of ${days.length} days open`,
      'font-weight:bold',
      STYLE_DIM
    )

    // Your rules, grouped by rule id so a recurring pattern reads as one entry.
    const ruleBlocked = blocked.filter((d) => d.isHatch)
    if (ruleBlocked.length > 0) {
      console.log(`%cBLOCKED BY HATCH RULES \u2014 ${ruleBlocked.length} day(s)`, STYLE_HATCH)
      const byRule = new Map<string, DayVerdict[]>()
      for (const day of ruleBlocked) {
        const key = day.ruleId ?? '(rule not identified)'
        const list = byRule.get(key)
        if (list) list.push(day)
        else byRule.set(key, [day])
      }
      for (const [ruleId, ruleDays] of byRule) {
        const def = hatch.ruleDefs[ruleId]
        console.log(`  ${ruleId}${def ? ` \u2014 "${def.text}"` : ''}`)
        console.log(`    \u2192 ${ruleDays.map((d) => formatDay(d.date)).join(', ')}`)
      }
    }

    // Facility settings, grouped by reason type.
    const facilityBlocked = blocked.filter((d) => !d.isHatch)
    if (facilityBlocked.length > 0) {
      console.log(`%cBLOCKED BY GOOSE SETTINGS \u2014 ${facilityBlocked.length} day(s)`, STYLE_GOOSE)
      const byReason = new Map<string, DayVerdict[]>()
      for (const day of facilityBlocked) {
        const key = day.reasonType ?? 'unknown'
        const list = byReason.get(key)
        if (list) list.push(day)
        else byReason.set(key, [day])
      }
      for (const [reasonType, reasonDays] of byReason) {
        console.log(`  ${reasonText(reasonType)}`)
        console.log(`    \u2192 ${reasonDays.map((d) => formatDay(d.date)).join(', ')}`)
      }
    }

    if (open.length > 0) {
      console.log(`%cOPEN \u2014 ${open.length} day(s)`, STYLE_DIM)
      console.log(`%c  ${open.map((d) => formatDay(d.date)).join(', ')}`, STYLE_DIM)
    }

    console.groupEnd()
  }

  console.groupEnd()
}

function printBlockedOffers(
  results: SearchResultLike[],
  hatch: HatchBlock | undefined,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>
): void {
  // Only offers with no open day at all. Partially blocked offers are still
  // bookable and are covered by the day-by-day section instead.
  const blocked = results.filter((r) => offerState(r, dayMap.get(r) ?? null) === 'blocked')
  if (blocked.length === 0) return

  console.groupCollapsed(`%c\u{1F6AB} BLOCKED OFFERS (${blocked.length})`, STYLE_HEADER)
  for (const result of blocked) {
    const availability = firstAvailability(result)
    const reason = availability?.unavailableReason
    const isHatch = isHatchSourced(reason)
    const hatchOffer = hatch?.offers.find((o) => o.offerId === result.offer?.id)

    console.group(`%c${offerLabel(result)}%c \u2014 "Call to Book"`, 'font-weight:bold', STYLE_DIM)

    const unavailableType = availability?.unavailableType
    if (unavailableType && UNAVAILABLE_TYPE_TEXT[unavailableType]) {
      console.log(`Why:    ${UNAVAILABLE_TYPE_TEXT[unavailableType]}`)
    } else {
      console.log(`Why:    ${reasonText(reason?.type)}`)
    }

    const blockedAddon = hatchOffer?.availability.addOns?.find((a) => a.available === false)
    if (unavailableType === 'REQUIRED_ADDON' && blockedAddon) {
      const addonName = blockedAddon.name ?? blockedAddon.id
      const blockingRule = blockedAddon.blockingRule
      console.log(
        `Chain:  Hatch rule blocked "${addonName}" \u2192 "${addonName}" is required on this offer \u2192 the offer cannot be booked`
      )
      if (blockingRule && hatch?.ruleDefs[blockingRule]) {
        console.log(`Rule:   ${blockingRule}\n        "${hatch.ruleDefs[blockingRule].text}"`)
      }
    } else if (isHatch && hatchOffer?.availability.winningRuleId) {
      const ruleId = hatchOffer.availability.winningRuleId
      const def = hatch?.ruleDefs[ruleId]
      console.log(`Rule:   ${ruleId}${def ? `\n        "${def.text}"` : ''}`)
    }

    if (reason?.min != null || reason?.max != null) {
      const limits = [reason.min != null ? `min ${reason.min}` : null, reason.max != null ? `max ${reason.max}` : null]
        .filter(Boolean)
        .join(', ')
      console.log(`Limit:  ${limits}`)
    }

    const pets = availability?.affectedByPets ?? hatchOffer?.availability.affectedPets
    if (pets && pets.length > 0) {
      const names = pets
        .map((p) => {
          const weight = (p as { weight?: number | null }).weight
          return weight != null ? `${p.displayName} (${weight} lbs)` : p.displayName
        })
        .join(', ')
      console.log(`Pets:   ${names}`)
    }

    const days = hatchOffer?.availability.days ?? []
    if (days.length > 0) {
      const blockedDays = days.filter((d) => !d.available)
      if (blockedDays.length === days.length) {
        console.log(`Days:   all ${days.length} blocked`)
      } else if (blockedDays.length > 0) {
        console.log(
          `Days:   ${blockedDays.length} of ${days.length} blocked \u2014 ${blockedDays.map((d) => d.date).join(', ')}`
        )
      }
    }

    if (availability?.unavailableMsg) {
      console.log(`Message shown: "${availability.unavailableMsg}"`)
    }

    console.log(
      isHatch ? '%cDecided by: HATCH RULES' : '%cDecided by: GOOSE SETTINGS',
      isHatch ? STYLE_HATCH : STYLE_GOOSE
    )
    console.groupEnd()
  }
  console.groupEnd()
}

function printAvailableOffers(
  results: SearchResultLike[],
  hatch: HatchBlock | undefined,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>
): void {
  const bookable = results.filter((r) => offerState(r, dayMap.get(r) ?? null) !== 'blocked')
  if (bookable.length === 0) return

  console.groupCollapsed(`%c\u2705 BOOKABLE OFFERS (${bookable.length})`, STYLE_HEADER)
  for (const result of bookable) {
    const hatchOffer = hatch?.offers.find((o) => o.offerId === result.offer?.id)
    const rate = firstAvailability(result)?.price?.rate
    const days = dayMap.get(result) ?? null
    const notes: string[] = []

    // Say up front that a bookable offer still has holes, and how many are ours.
    if (offerState(result, days) === 'partial' && days) {
      const blockedDays = days.filter((d) => !d.available)
      const byRules = blockedDays.filter((d) => d.isHatch).length
      notes.push(
        byRules > 0
          ? `${blockedDays.length} of ${days.length} days unavailable, ${byRules} because of Hatch rules`
          : `${blockedDays.length} of ${days.length} days unavailable, all Goose settings`
      )
    }
    if (hatchOffer?.availability.baselineAvailable === false) notes.push('unlocked by one of your allow rules')
    if (result.hasRequiredAddons) notes.push('has required add-ons')

    console.log(
      `${offerLabel(result)}${rate != null ? ` \u2014 $${rate}` : ''}${notes.length ? ` (${notes.join('; ')})` : ''}`
    )
  }
  console.groupEnd()
}

function printPricing(hatch: HatchBlock, apiType: 'client' | 'admin'): void {
  const repriced = hatch.offers.filter((o) => o.pricing?.changed)

  console.groupCollapsed(
    `%c\u{1F4B0} PRICING (${repriced.length} offer${repriced.length === 1 ? '' : 's'} adjusted)`,
    STYLE_HEADER
  )

  if (apiType === 'client') {
    console.log(
      '%c\u2139\uFE0F On the customer booking page, prices are CALCULATED but NOT applied. The figures below are what Hatch rules would do \u2014 the customer still sees the Goose price.',
      STYLE_INFO
    )
  }

  if (repriced.length === 0) {
    console.log('%cNo pricing rules changed any price in this search.', STYLE_DIM)
  }

  for (const offer of repriced) {
    const p = offer.pricing
    console.group(`%c${offer.offerName}`, 'font-weight:bold')
    console.log(`Goose price: $${p.baseRate}/night \u00B7 $${p.baseTotal} total`)
    console.log(
      `With Hatch rules: $${p.finalRate}/night \u00B7 $${p.finalTotal} total (${p.delta >= 0 ? '+' : ''}$${p.delta})`
    )
    for (const step of p.steps ?? []) {
      const sign = step.kind === 'surcharge' ? '+' : '\u2212'
      console.log(`  ${step.kind}: ${sign}$${Math.abs(step.impact)} \u2014 ${step.label ?? step.ruleId}`)
    }
    if (p.losingCandidates && p.losingCandidates.length > 0) {
      console.log(
        `%c  ${p.losingCandidates.length} other pricing rule(s) matched but lost (highest amount wins): ${p.losingCandidates.map((c) => c.ruleId).join(', ')}`,
        STYLE_DIM
      )
    }
    console.groupEnd()
  }
  console.groupEnd()
}

/**
 * Map offer id → customer-facing name.
 *
 * The sidecar carries the internal slug (`no-tt-daycare`) while the response has
 * the display name (`No TT Daycare`). Owners recognise the latter, so rule output
 * is resolved through this rather than echoing the slug.
 */
function displayNameIndex(results: SearchResultLike[]): Map<string, string> {
  const index = new Map<string, string>()
  for (const result of results) {
    const id = result.offer?.id
    const name = result.offer?.displayName
    if (id && name) index.set(id, name)
  }
  return index
}

function printRulesFired(hatch: HatchBlock, names: Map<string, string>): void {
  const fired = hatch.rules.filter((r) => r.status === 'matched')
  if (fired.length === 0) {
    console.log('%c\u{1F4D6} NO RULES FIRED in this search', STYLE_DIM)
    return
  }

  console.groupCollapsed(`%c\u{1F4D6} RULES THAT FIRED (${fired.length})`, STYLE_HEADER)
  for (const rule of fired) {
    const def = hatch.ruleDefs[rule.ruleId]
    console.group(`%c${rule.ruleId}`, 'font-weight:bold')
    if (def) console.log(`"${def.text}"`)
    console.log(`Effect: ${rule.effect ?? '?'} (${rule.area ?? '?'})`)
    if (rule.affectedOffers?.length)
      console.log(`Applied to: ${rule.affectedOffers.map((o) => names.get(o.id) ?? o.name ?? o.id).join(', ')}`)
    if (rule.matchedPets?.length)
      console.log(`Triggered by: ${rule.matchedPets.map((p) => p.displayName ?? p.id).join(', ')}`)
    console.groupEnd()
  }
  console.groupEnd()
}

function printRulesSkipped(hatch: HatchBlock): void {
  const skipped = hatch.rules.filter((r) => r.status === 'skipped')
  if (skipped.length === 0) return

  console.groupCollapsed(`%c\u23ED\uFE0F RULES THE ENGINE COULD NOT CHECK (${skipped.length})`, STYLE_WARN)
  for (const rule of skipped) {
    const def = hatch.ruleDefs[rule.ruleId]
    const explain = rule.skipReason ? SKIP_TEXT[rule.skipReason] : undefined
    console.group(`%c${rule.ruleId}`, 'font-weight:bold')
    if (def) console.log(`"${def.text}"`)
    if (explain) {
      console.log(`Problem: this rule ${explain.what}`)
      console.log(`Effect:  ${explain.fix}`)
    } else {
      console.log(`Problem: ${rule.skipReason ?? 'unknown'}`)
    }
    if (rule.skipDetail) console.log(`%cDetail:  ${rule.skipDetail}`, STYLE_DIM)
    console.groupEnd()
  }
  console.groupEnd()
}

function printRulesThatDidNothing(location: string, hatch: HatchBlock, requestType?: string): void {
  const known = ruleRegistry.get(location)
  if (!known || known.size === 0) return

  const active = new Set(hatch.rules.map((r) => r.ruleId))
  const idle = [...known.entries()].filter(([ruleId]) => !active.has(ruleId))
  if (idle.length === 0) return

  const locationAddonRules = addonRuleIds.get(location) ?? new Set<string>()
  const isPrimaryRequest = !requestType || requestType === 'PRIMARY'

  console.groupCollapsed(`%c\u{1F47B} RULES THAT DID NOTHING IN THIS SEARCH (${idle.length})`, STYLE_DIM)
  console.log(
    '%cThese rules exist but changed nothing here. Seen in earlier searches this session \u2014 the response itself does not list them.',
    STYLE_DIM
  )
  for (const [ruleId, def] of idle) {
    console.group(`%c${ruleId}`, 'font-weight:bold')
    console.log(`"${def.text}"`)
    if (locationAddonRules.has(ruleId) && isPrimaryRequest) {
      console.log('Why: this rule targets an add-on, so it only applies at the add-on step of the booking')
    } else {
      console.log('Why: no pet or offer in this search matched its conditions, or it targets a different offer')
    }
    console.groupEnd()
  }
  console.groupEnd()
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PROBLEMS (printed outside the group)
   ═══════════════════════════════════════════════════════════════════════════════ */

function collectProblems(hatch: HatchBlock): Array<{ level: 'problem' | 'warn'; message: string }> {
  const out: Array<{ level: 'problem' | 'warn'; message: string }> = []

  for (const rule of hatch.rules) {
    if (rule.status !== 'skipped' || rule.skipReason !== 'incomplete_data') continue
    const def = hatch.ruleDefs[rule.ruleId]
    out.push({
      level: 'warn',
      message: `Missing pet data blocked this booking. Rule ${rule.ruleId}${def ? ` ("${def.text}")` : ''} could not be checked${rule.skipDetail ? ` \u2014 ${rule.skipDetail}` : ''}. Fill in the missing data to unblock.`,
    })
  }

  for (const rule of hatch.rules) {
    if (rule.status !== 'matched' || !rule.affectedOffers?.length) continue
    for (const target of rule.affectedOffers) {
      const offer = hatch.offers.find((o) => o.offerId === target.id)
      if (!offer) continue
      if (offer.availability.baselineAvailable === false && !offer.changed) {
        out.push({
          level: 'warn',
          message: `Rule ${rule.ruleId} matched "${offer.offerName}" but Goose had already blocked it \u2014 the Hatch rule had no effect here.`,
        })
      }
    }
  }

  return out
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SESSION REGISTRY
   ═══════════════════════════════════════════════════════════════════════════════ */

function recordRules(location: string, hatch: HatchBlock, results: SearchResultLike[]): void {
  let known = ruleRegistry.get(location)
  if (!known) {
    known = new Map()
    ruleRegistry.set(location, known)
  }
  for (const [ruleId, def] of Object.entries(hatch.ruleDefs ?? {})) {
    known.set(ruleId, def)
  }

  const primaryIds = new Set(results.filter((r) => (r.offer?.type ?? 'PRIMARY') === 'PRIMARY').map((r) => r.offer?.id))
  let addons = addonRuleIds.get(location)
  if (!addons) {
    addons = new Set()
    addonRuleIds.set(location, addons)
  }
  for (const rule of hatch.rules) {
    if (rule.status !== 'matched' || !rule.affectedOffers?.length) continue
    if (rule.affectedOffers.some((o) => !primaryIds.has(o.id))) addons.add(rule.ruleId)
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   RESPONSE HISTORY — stores recent responses for on-demand detailed inspection
   ═══════════════════════════════════════════════════════════════════════════════ */

interface StoredReport {
  input: HatchDiagnosticsContext
  response: unknown
  timestamp: number
  header: string
}

const MAX_HISTORY = 20
const history: StoredReport[] = []

/**
 * Print full diagnostics for `count` most recent stored responses.
 * Called from the console via `hatch()` / `hatch(n)`.
 */
export function printDetailed(count: number): void {
  if (history.length === 0) {
    console.log('%c\u{1F423} HATCH%c \u00B7 No searches recorded yet.', STYLE_HATCH, STYLE_DIM)
    return
  }

  const n = Math.min(Math.max(count, 1), history.length)
  const slice = history.slice(-n)

  console.log(
    `%c\u{1F423} HATCH%c \u00B7 Showing ${n} of ${history.length} recorded search${history.length === 1 ? '' : 'es'}`,
    STYLE_HATCH,
    STYLE_DIM
  )

  for (const entry of slice) {
    printFullReport(entry.input, entry.response)
  }
}

/** The full detailed report — only called on demand via hatch(). */
function printFullReport(input: HatchDiagnosticsContext, response: unknown): void {
  try {
    const resp = response as ResponseLike | undefined
    const hatch = resp?.hatch
    const results = resp?.results ?? []

    if (!hatch) {
      console.log(
        `%c\u{1F423} HATCH%c \u00B7 ${input.serviceTypeName} \u00B7 no sidecar (not routed through Hatch)`,
        STYLE_HATCH,
        STYLE_DIM
      )
      return
    }

    if (!hatch.evaluated) {
      console.log(
        `%c\u{1F423} HATCH%c \u00B7 ${input.serviceTypeName} \u00B7 no rules deployed \u2014 the response is the Goose baseline.`,
        STYLE_HATCH,
        STYLE_DIM
      )
      return
    }

    const dayMap = new Map<SearchResultLike, DayVerdict[] | null>()
    for (const result of results) {
      const hatchOffer = hatch.offers.find((o) => o.offerId === result.offer?.id)
      dayMap.set(result, collectDayVerdicts(result, hatchOffer, input.params?.startDate))
    }

    const states = results.map((r) => offerState(r, dayMap.get(r) ?? null))
    const blockedCount = states.filter((s) => s === 'blocked').length
    const partialCount = states.filter((s) => s === 'partial').length
    const ruleBlockedDays = results.reduce((total, result) => {
      const days = dayMap.get(result)
      if (!days) return total
      return total + days.filter((d) => !d.available && d.isHatch).length
    }, 0)

    console.group(
      `%c\u{1F423} HATCH%c \u00B7 ${buildHeader(input, hatch, blockedCount, partialCount, ruleBlockedDays)}`,
      STYLE_HATCH,
      ''
    )

    printCustomerView(results, hatch, dayMap)
    printBlockedOffers(results, hatch, dayMap)
    printDayBreakdown(results, hatch, dayMap)
    printAvailableOffers(results, hatch, dayMap)
    printPricing(hatch, input.apiType)
    printRulesFired(hatch, displayNameIndex(results))
    printRulesSkipped(hatch)
    printRulesThatDidNothing(input.location, hatch, input.params?.type)

    console.groupCollapsed('%c\u{1F527} Raw sidecar', STYLE_DIM)
    console.log(hatch)
    console.groupEnd()

    console.groupEnd()
  } catch {
    // Diagnostics must never break anything.
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   REPORT ENTRY POINT (always-on: one-line summary only)
   ═══════════════════════════════════════════════════════════════════════════════ */

function report(input: HatchDiagnosticsContext, response: unknown): void {
  try {
    if (typeof console === 'undefined' || typeof console.groupCollapsed !== 'function') return

    const resp = response as ResponseLike | undefined
    const hatch = resp?.hatch
    const results = resp?.results ?? []

    resetIfLocationChanged(input.location)

    if (!hatch) {
      const key = `${input.serviceTypeName}:${input.endpoint}:${input.params?.type ?? ''}:nosidecar`
      if (printedHashes.get(key) !== 'x') {
        printedHashes.set(key, 'x')
        console.log(
          `%c\u{1F423} HATCH%c \u00B7 ${input.serviceTypeName} \u00B7 no sidecar on the response (this location is not routed through Hatch)`,
          STYLE_HATCH,
          STYLE_DIM
        )
      }
      return
    }

    const hash = hashReport(hatch, results)
    const dedupeKey = [
      input.serviceTypeName,
      input.endpoint,
      input.params?.type ?? '',
      input.parentOffer ?? '',
      input.params?.startDate ?? '',
      input.params?.endDate ?? '',
    ].join(':')
    const previous = printedHashes.get(dedupeKey)
    if (previous === hash) return
    printedHashes.set(dedupeKey, hash)

    recordRules(input.location, hatch, results)

    // Compute the one-line summary for the always-on output.
    const dayMap = new Map<SearchResultLike, DayVerdict[] | null>()
    for (const result of results) {
      const hatchOffer = hatch.offers.find((o) => o.offerId === result.offer?.id)
      dayMap.set(result, collectDayVerdicts(result, hatchOffer, input.params?.startDate))
    }
    const states = results.map((r) => offerState(r, dayMap.get(r) ?? null))
    const blockedCount = states.filter((s) => s === 'blocked').length
    const partialCount = states.filter((s) => s === 'partial').length
    const ruleBlockedDays = results.reduce((total, result) => {
      const days = dayMap.get(result)
      if (!days) return total
      return total + days.filter((d) => !d.available && d.isHatch).length
    }, 0)

    const header = buildHeader(input, hatch, blockedCount, partialCount, ruleBlockedDays)

    // Problems still print immediately — they should not be missed.
    const problems = collectProblems(hatch)
    for (const problem of problems) {
      console.log(
        `%c${problem.level === 'problem' ? '\u{1F534}' : '\u26A0\uFE0F'} HATCH%c \u00B7 ${problem.message}`,
        problem.level === 'problem' ? STYLE_PROBLEM : STYLE_WARN,
        ''
      )
    }

    // One-line summary — always visible. Type hatch() in the console for details.
    console.log(`%c\u{1F423} HATCH%c \u00B7 ${header}`, STYLE_HATCH, '')

    // Store for on-demand inspection.
    history.push({ input, response, timestamp: Date.now(), header })
    if (history.length > MAX_HISTORY) history.shift()
  } catch {
    // Diagnostics must never break a booking search.
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   GLOBAL hatch() FUNCTION — type in the console for detailed output
   ═══════════════════════════════════════════════════════════════════════════════ */

if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).hatch = (count?: number) => {
    printDetailed(count ?? 1)
  }
}
